// File: app/src/utils/repEstimator.js
//
// Pure module — estimates per-rep duration for rep-based Work nodes using a
// Velocity-Based Training (VBT) formula for barbell lifts and hardcoded
// baselines for functional movements. Optionally blends with historical
// actuals once enough samples exist.
//
// No React, no I/O, no module-level state.

import { isCalorieUnit } from "./planNodeUtils";
import { EXERCISES, exerciseOf, resolveExercise } from "../config/exercises";

// ---------------------------------------------------------------------------
// Exercise key normalisation
// ---------------------------------------------------------------------------
//
// Labels resolve through the canonical exercise library (config/exercises.js,
// #84). Unknown labels fall back to the old heuristic — strip equipment
// qualifiers, take the last word — so a label the library has never seen
// still gets a stable key for its own pace history.

const EQUIPMENT_QUALIFIERS = new Set([
  "barbell",
  "bb",
  "dumbbell",
  "db",
  "kettlebell",
  "kb",
  "cable",
  "machine",
  "smith",
  "heavy",
  "light",
  "weighted",
  "bodyweight",
  "bw",
]);

const legacyKey = (label) => {
  const tokens = String(label)
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((tok) => !EQUIPMENT_QUALIFIERS.has(tok));
  return tokens[tokens.length - 1] || "default";
};

export const normaliseExerciseKey = (label) => {
  if (!label) return "default";
  return resolveExercise(label)?.key || legacyKey(label);
};

/** The key for a plan step: its tag or library match, else as above. */
export const exerciseKeyOf = (node) =>
  exerciseOf(node)?.key || normaliseExerciseKey(node?.label);

// ---------------------------------------------------------------------------
// Baselines (per-rep seconds for "standard" pace at ~80% effort)
// ---------------------------------------------------------------------------

// Each entry: { eccentric, pause, concentric } in seconds, from the library's
// per-exercise timing. concentric is the value used when VBT cannot resolve a
// real load percentage.
const DEFAULT_BASELINE = { eccentric: 1.5, pause: 0.0, concentric: 1.5 };
const BASELINES = Object.fromEntries([
  ...EXERCISES.filter((e) => e.timing).map((e) => [e.key, e.timing]),
  ["default", DEFAULT_BASELINE],
]);

// VBT constants for barbell-style lifts:
//   v0   = theoretical velocity at zero load (m/s)
//   vmvt = minimum velocity threshold at 100% 1RM (m/s)
//   rom  = concentric range of motion (m) for an average-height lifter
const VBT_CONSTANTS = {
  squat: { v0: 1.3, vmvt: 0.3, rom: 0.6 },
  bench: { v0: 1.1, vmvt: 0.15, rom: 0.4 },
  deadlift: { v0: 1.2, vmvt: 0.15, rom: 0.5 },
  ohp: { v0: 1.15, vmvt: 0.2, rom: 0.5 },
};

const PACE_MULTIPLIER = {
  fast: 0.8,
  standard: 1.0,
  controlled: 1.2,
};

// ---------------------------------------------------------------------------
// Load resolution (hybrid fallback)
// ---------------------------------------------------------------------------

// Resolve the working load percentage (0..1) for a node. Order of precedence:
//   1. node.intensityPct (LLM-emitted % of 1RM)
//   2. node.weight / profile.oneRms[key]
//   3. profile.workingWeights[key] / profile.oneRms[key]
//   4. null  (caller falls back to the baseline concentric time)
//
// Returns null on any zero-division or missing-key scenario so NaN never
// propagates into totalDuration.
export const resolveLoadPercent = (node, trainingData, exerciseKey) => {
  if (node && Number.isFinite(node.intensityPct) && node.intensityPct > 0) {
    return Math.max(0, Math.min(1, node.intensityPct / 100));
  }

  const oneRm = trainingData?.oneRms?.[exerciseKey];
  if (!Number.isFinite(oneRm) || oneRm <= 0) return null;

  if (node && Number.isFinite(node.weight) && node.weight > 0) {
    return Math.max(0, Math.min(1, node.weight / oneRm));
  }

  const working = trainingData?.workingWeights?.[exerciseKey];
  if (Number.isFinite(working) && working > 0) {
    return Math.max(0, Math.min(1, working / oneRm));
  }

  return null;
};

// ---------------------------------------------------------------------------
// VBT formula
// ---------------------------------------------------------------------------

// Concentric time in seconds for a barbell lift at load percentage P (0..1).
// t_c = rom / (v0 - P × (v0 - vmvt))
// Returns null if the lift is not in VBT_CONSTANTS or denominator is invalid.
export const vbtConcentricSec = (loadPct, exerciseKey) => {
  const c = VBT_CONSTANTS[exerciseKey];
  if (!c) return null;
  if (loadPct == null || !Number.isFinite(loadPct)) return null;
  const v = c.v0 - loadPct * (c.v0 - c.vmvt);
  if (!Number.isFinite(v) || v <= 0) return null;
  const t = c.rom / v;
  return Number.isFinite(t) && t > 0 ? t : null;
};

// ---------------------------------------------------------------------------
// History blend
// ---------------------------------------------------------------------------

const BLEND_MIN_SAMPLES = 3;
const BLEND_SOFTNESS = 5; // wH = samples / (samples + softness), capped at 0.8

// Weighted blend between the formula estimate and history-derived per-rep.
// Returns { perRepSec, source } where source ∈ "formula" | "blended" | "history".
const blendWithHistory = (formulaSec, historyEntry) => {
  if (
    !historyEntry ||
    !Number.isFinite(historyEntry.perRepSec) ||
    historyEntry.perRepSec <= 0 ||
    !Number.isFinite(historyEntry.samples) ||
    historyEntry.samples < BLEND_MIN_SAMPLES
  ) {
    return { perRepSec: formulaSec, source: "formula" };
  }

  const wH = Math.min(
    historyEntry.samples / (historyEntry.samples + BLEND_SOFTNESS),
    0.8,
  );
  const wF = 1 - wH;
  const blended = formulaSec * wF + historyEntry.perRepSec * wH;
  return {
    perRepSec: blended,
    source: wH >= 0.79 ? "history" : "blended",
  };
};

// ---------------------------------------------------------------------------
// Public estimator
// ---------------------------------------------------------------------------

// Pure formula estimate: baseline eccentric/pause + (VBT concentric or
// baseline concentric) × pace multiplier. No history blend.
const formulaPerRepSec = (exerciseKey, loadPct, paceMultiplier) => {
  const baseline = BASELINES[exerciseKey] || BASELINES.default;
  const concentric = vbtConcentricSec(loadPct, exerciseKey) ?? baseline.concentric;
  const raw = baseline.eccentric + baseline.pause + concentric;
  return raw * paceMultiplier;
};

/**
 * Estimate per-rep seconds for a rep-based Work node.
 *
 * @param {Object} node          Workout plan leaf node ({ label, value, unit, weight?, intensityPct? }).
 * @param {Object} trainingData  Derived training data ({ pace, oneRms, tempos, workingWeights }); may be {} or undefined.
 * @param {Object} historyMap    { exerciseKey: { perRepSec, samples } } from useExerciseHistory.
 * @returns {{ perRepSec: number, source: string, exerciseKey: string, loadPct: number|null }}
 *          source ∈ "vbt" | "baseline" | "fallback" | "tempo" | "blended" | "history".
 */
export const estimatePerRepSec = (node, trainingData, historyMap) => {
  const exerciseKey = exerciseKeyOf(node);
  const paceKey = trainingData?.pace || "standard";
  const paceMultiplier = PACE_MULTIPLIER[paceKey] ?? PACE_MULTIPLIER.standard;
  const loadPct = resolveLoadPercent(node, trainingData, exerciseKey);

  const formulaSec = formulaPerRepSec(exerciseKey, loadPct, paceMultiplier);

  // A user-supplied per-rep tempo (a Performance Profile row with sec_per_rep)
  // overrides the formula for ANY exercise, scaled by the global pace
  // multiplier. Real logged history still refines it via blendWithHistory.
  const userTempo = trainingData?.tempos?.[exerciseKey];
  const usedTempo = Number.isFinite(userTempo) && userTempo > 0;
  const base = usedTempo ? userTempo * paceMultiplier : formulaSec;

  const blend = blendWithHistory(base, historyMap?.[exerciseKey]);

  let source = blend.source;
  if (source === "formula") {
    if (usedTempo) source = "tempo";
    else if (loadPct != null && VBT_CONSTANTS[exerciseKey]) source = "vbt";
    else if (BASELINES[exerciseKey]) source = "baseline";
    else source = "fallback";
  }

  return {
    perRepSec: Math.max(0, blend.perRepSec),
    source,
    exerciseKey,
    loadPct,
  };
};

// ---------------------------------------------------------------------------
// Calorie work
// ---------------------------------------------------------------------------
//
// A calorie target ("100 cal Assault Bike") is counted work like reps: the
// machine's own display is the referee and the phase ends when the user hits
// the number, so the duration below is a PROJECTION only.
//
// Seconds per calorie at a sustainable working effort. An air bike burns far
// faster than a rower for the same wall-clock minute — the fan resistance rises
// with the cube of speed — so the machine matters more here than it does for
// distance. Ordered; first label match wins.
const CAL_PACE = [
  { re: /(assault|echo|air\s*bike)/, secPerCal: 3.0 },
  { re: /(bike\s*erg|watt\s*bike|\bbike\b|\bcycl)/, secPerCal: 3.6 },
  { re: /(row|rower|\berg\b|concept\s*2|\bc2\b)/, secPerCal: 4.0 },
  { re: /(ski\s*erg|skierg|\bski\b)/, secPerCal: 4.4 },
  { re: /(run|jog|sprint|tread)/, secPerCal: 4.0 },
];
const DEFAULT_SEC_PER_CAL = 4.0;

// Calorie history is bucketed under its own key namespace. The same machine is
// routinely prescribed both ways ("Row 250 m" / "Row 20 cal"), and a
// sec-per-cal sample read back as sec-per-rep would poison the rep estimate for
// that label — so the two never share a bucket.
export const CAL_HISTORY_PREFIX = "cal:";

export const calHistoryKey = (label) =>
  `${CAL_HISTORY_PREFIX}${normaliseExerciseKey(label)}`;

/**
 * Estimate per-calorie seconds for a calorie-based Work node.
 *
 * @param {Object} node          Leaf node ({ label, value, unit: "cal" }).
 * @param {Object} trainingData  Derived training data; may be {} or undefined.
 * @param {Object} historyMap    { exerciseKey: { perRepSec, samples } }.
 * @returns {{ perCalSec: number, source: string, exerciseKey: string, loadPct: null }}
 *          source ∈ "ergo" | "fallback" | "blended" | "history".
 */
export const estimatePerCalSec = (node, trainingData, historyMap) => {
  const label = String(node?.label || "").toLowerCase();
  const hit = CAL_PACE.find((e) => e.re.test(label));
  const paceKey = trainingData?.pace || "standard";
  const paceMultiplier = PACE_MULTIPLIER[paceKey] ?? PACE_MULTIPLIER.standard;
  const base = (hit?.secPerCal ?? DEFAULT_SEC_PER_CAL) * paceMultiplier;

  const exerciseKey = calHistoryKey(node?.label);
  const blend = blendWithHistory(base, historyMap?.[exerciseKey]);

  return {
    perCalSec: Math.max(0, blend.perRepSec),
    source: blend.source === "formula" ? (hit ? "ergo" : "fallback") : blend.source,
    exerciseKey,
    loadPct: null,
  };
};

/**
 * Estimate total seconds for a counted phase (reps or calories), by
 * per-unit estimate × node.value. Returns 0 for anything else.
 *
 * @param {Object} node          Counted leaf node.
 * @param {Object} trainingData  profile.training_data.
 * @param {Object} historyMap    History map.
 * @returns {number} Estimated phase duration in seconds (>= 0).
 */
export const estimatePhaseSec = (node, trainingData, historyMap) => {
  const count = Number(node?.value);
  if (!Number.isFinite(count) || count <= 0) return 0;
  if (isCalorieUnit(node?.unit)) {
    const { perCalSec } = estimatePerCalSec(node, trainingData, historyMap);
    return perCalSec * count;
  }
  const { perRepSec } = estimatePerRepSec(node, trainingData, historyMap);
  return perRepSec * count;
};

// Exposed for tests + debug surfaces.
export const __internals = {
  BASELINES,
  VBT_CONSTANTS,
  PACE_MULTIPLIER,
  BLEND_MIN_SAMPLES,
  BLEND_SOFTNESS,
};
